import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { login, verify2FA } from '../services/api';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState(new Array(6).fill(""));
    const [recoveryCode, setRecoveryCode] = useState('');
    const [useRecovery, setUseRecovery] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [show2FA, setShow2FA] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    // Clear localStorage on component mount to ensure a clean login state
    useEffect(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const response = await login(email, password);
            if (response.data.two_fa_required) {
                setShow2FA(true);
            } else {
                if (response.data.access_token && response.data.user) {
                    localStorage.setItem('token', response.data.access_token);
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                    window.dispatchEvent(new Event('localStorageChanged'));
                    navigate('/');
                } else {
                    setError('Login failed: Invalid response from server.');
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Invalid credentials or inactive account.');
        } finally {
            setLoading(false);
        }
    };

    const handleVerify2FA = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        let payload = { email };
        
        if (useRecovery) {
            if (!recoveryCode) {
                setError('Please enter a recovery code.');
                setLoading(false);
                return;
            }
            payload.recovery_code = recoveryCode;
        } else {
            const otpString = otp.join("");
            if (otpString.length !== 6) {
                setError('Please enter a complete 6-digit code.');
                setLoading(false);
                return;
            }
            payload.otp = otpString;
        }

        try {
            const response = await verify2FA(payload);
            if (response.data.access_token && response.data.user) {
                localStorage.setItem('token', response.data.access_token);
                localStorage.setItem('user', JSON.stringify(response.data.user));
                window.dispatchEvent(new Event('localStorageChanged'));
                navigate('/');
            } else {
                setError('Verification failed: Invalid response from server.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Invalid code.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (element, index) => {
        if (isNaN(element.value)) return false;

        const newOtp = [...otp];
        newOtp[index] = element.value;
        setOtp(newOtp);

        // Focus next input
        if (element.value && element.nextElementSibling) {
            element.nextElementSibling.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace") {
            if (!otp[index] && e.target.previousElementSibling) {
                e.target.previousElementSibling.focus();
            }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6);
        if (/^\d+$/.test(pastedData)) {
            const newOtp = [...otp];
            pastedData.split('').forEach((char, i) => {
                if (i < 6) newOtp[i] = char;
            });
            setOtp(newOtp);
            // Focus the last filled input or the next empty one
            const nextIndex = Math.min(pastedData.length, 5);
            const inputs = document.querySelectorAll('input[name="otp"]');
            if (inputs[nextIndex]) {
                inputs[nextIndex].focus();
            }
        }
    };

    return (
        <div id="auth-page">
            <div className="auth-card">
                <div className="icon-container">🛍️</div>
                <div className="auth-header">
                    <h3>Welcome Back!</h3>
                    <p className="text-muted">Sign in to continue to SmartShop.</p>
                </div>

                {error && <div className="alert alert-danger">{error}</div>}

                {!show2FA ? (
                    <form onSubmit={handleLogin}>
                        <div className="mb-3 text-start">
                            <label className="form-label">Email Address</label>
                            <input 
                                type="email" 
                                className="form-control" 
                                value={email} 
                                onChange={(e) => setEmail(e.target.value)} 
                                required 
                            />
                        </div>
                        <div className="mb-3 text-start">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="form-label mb-0">Password</label>
                                <Link to="/forgot-password" className="text-primary small text-decoration-none fw-bold">
                                    Forgot Password?
                                </Link>
                            </div>
                            <div className="position-relative">
                                <input 
                                    type={showPassword ? "text" : "password"} 
                                    className="form-control" 
                                    value={password} 
                                    onChange={(e) => setPassword(e.target.value)} 
                                    required 
                                />
                                <button 
                                    type="button"
                                    className="btn border-0 position-absolute end-0 top-50 translate-middle-y me-2"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{ zIndex: 10 }}
                                >
                                    <i className={`bi bi-eye${showPassword ? '-slash' : ''}`}></i>
                                </button>
                            </div>
                        </div>
                        <button type="submit" className="btn-auth" disabled={loading}>
                            {loading ? 'Signing In...' : 'Sign In'}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleVerify2FA}>
                        {!useRecovery ? (
                            <>
                                <div className="mb-3 text-start">
                                    <label className="form-label">Enter 6-Digit Code</label>
                                    <p className="text-muted small">Enter the code from your authenticator app for {email}.</p>
                                    <div className="d-flex gap-2 justify-content-center mb-3">
                                        {otp.map((data, index) => {
                                            return (
                                                <input
                                                    className="form-control text-center fs-4"
                                                    type="text"
                                                    name="otp"
                                                    maxLength="1"
                                                    key={index}
                                                    value={data}
                                                    onChange={e => handleChange(e.target, index)}
                                                    onKeyDown={e => handleKeyDown(e, index)}
                                                    onPaste={handlePaste}
                                                    onFocus={e => e.target.select()}
                                                    style={{ 
                                                        width: '45px', 
                                                        height: '55px', 
                                                        border: '2px solid #dee2e6', 
                                                        borderRadius: '8px' 
                                                    }}
                                                    required={index === 0}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="text-center mb-3">
                                    <button 
                                        type="button" 
                                        className="btn btn-link text-decoration-none"
                                        onClick={() => setUseRecovery(true)}
                                    >
                                        Use a recovery code
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="mb-3 text-start">
                                    <label className="form-label">Recovery Code</label>
                                    <p className="text-muted small">Enter one of your emergency recovery codes.</p>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        value={recoveryCode} 
                                        onChange={(e) => setRecoveryCode(e.target.value)} 
                                        placeholder="e.g. xxxxx-xxxxx"
                                        required 
                                    />
                                </div>
                                <div className="text-center mb-3">
                                    <button 
                                        type="button" 
                                        className="btn btn-link text-decoration-none"
                                        onClick={() => setUseRecovery(false)}
                                    >
                                        Use authenticator code
                                    </button>
                                </div>
                            </>
                        )}
                        
                        <button type="submit" className="btn-auth" disabled={loading}>
                            {loading ? 'Verifying...' : 'Verify'}
                        </button>
                    </form>
                )}

                <div className="mt-4 text-center">
                    <small className="text-muted">
                        Don't have an account? <Link to="/register" className="hover-underline">Sign up</Link>
                    </small>
                </div>
            </div>
        </div>
    );
};

export default Login;
